import { createApp, h, reactive } from "vue";

/**
 * Keeps the Web Analytics overlay attached after the move to Vue 3.
 *
 * The overlay is ga4/panel.js (public/ga4, loaded by index.html, not bundled).
 * It takes over the
 * "deal-analytics" page by watching store.state.common.location, and it finds
 * the store the way a Vue 2 page exposed it: the first element whose
 * `__vue__.$store` has `state.common`. Vue 3 sets no `__vue__`, so without this
 * the page would silently go blank.
 *
 * Exposing exactly that on the mount element keeps panel.js working
 * unchanged. Nothing in the panel reads it.
 */
export function exposeStoreToGa4Overlay(el, store) {
  if (!el) return;
  Object.defineProperty(el, "__vue__", {
    value: Object.freeze({ $store: store }),
    configurable: true,
  });
}

/**
 * The overlay picks its range with the panel's own calendar (src/ui/DatePicker.vue), so the
 * Online Orders page works like every other page. panel.js is not bundled and cannot import it,
 * so it gets one mount function on window instead:
 *
 *   var picker = window.ConnectDatePicker.mountRange(el, { start, end, maxDays, label, onChange: function (start, end) {} });
 *   picker.set(start, end);   // the range changed outside the calendar (data.php narrowed it)
 *   picker.destroy();
 */
export function exposeDatePickerToGa4Overlay(DatePicker) {
  window.ConnectDatePicker = Object.freeze({
    mountRange(el, options = {}) {
      const state = reactive({ start: options.start || "", end: options.end || "" });
      const app = createApp({
        render: () =>
          h(DatePicker, {
            range: true,
            bare: true,
            start: state.start,
            end: state.end,
            maxDays: options.maxDays || 0,
            label: options.label || "",
            "onUpdate:start": (value) => (state.start = value),
            "onUpdate:end": (value) => (state.end = value),
            onChange: () => options.onChange && options.onChange(state.start, state.end),
          }),
      });
      app.mount(el);
      return {
        set(start, end) {
          state.start = start;
          state.end = end;
        },
        destroy() {
          app.unmount();
        },
      };
    },
  });
}
